import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api from '../lib/api';
import { Brain, Box, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { trackSignup } from '../lib/analytics';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg('Invalid verification link.');
      return;
    }

    const verify = async () => {
      try {
        await api.post('/auth/verify-email', { token });
        setStatus('success');
        // Fire CompleteRegistration on email confirmation as well — this is
        // the user's preferred attribution moment (they're proven-real here,
        // not just signed-up). The session-level dedup in trackSignup keeps
        // it from double-counting if both Register.js and VerifyEmail.js fire.
        try { await trackSignup('email_verified'); } catch (_) {}
      } catch (err) {
        setStatus('error');
        setErrorMsg(err.response?.data?.detail || 'Verification failed. The link may have expired.');
      }
    };

    verify();
  }, [token]);

  return (
    <div className="min-h-screen bg-bg-dark flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-3">
            <div className="relative">
              <Brain className="w-8 h-8 text-primary" />
              <Box className="w-4 h-4 text-accent absolute -bottom-1 -right-1" />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              PrintMind AI
            </span>
          </Link>
        </div>

        <div className="bg-bg-light rounded-xl border border-text-muted/20 p-8" data-testid="verify-email-card">
          {status === 'verifying' && (
            <div className="text-center space-y-4">
              <Loader2 className="w-12 h-12 text-primary mx-auto animate-spin" />
              <h2 className="text-xl font-bold text-text-primary">Verifying your email...</h2>
              <p className="text-sm text-text-secondary">Please wait a moment.</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center space-y-4">
              <div className="w-14 h-14 bg-green-500/15 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-7 h-7 text-green-400" />
              </div>
              <h2 className="text-xl font-bold text-text-primary">Email Verified!</h2>
              <p className="text-sm text-text-secondary">
                Your account is now fully activated. You can start using all PrintMind AI features.
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition text-sm"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </button>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center space-y-4">
              <div className="w-14 h-14 bg-red-500/15 rounded-full flex items-center justify-center mx-auto">
                <AlertCircle className="w-7 h-7 text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-text-primary">Verification Failed</h2>
              <p className="text-sm text-text-secondary">{errorMsg}</p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => navigate('/login')}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition text-sm"
                >
                  Go to Login
                </button>
                <p className="text-xs text-text-muted">
                  Log in and request a new verification email from your dashboard.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
